import { MenuItemType, MenuType } from "@/types/baseTypes";
import React, { useRef } from "react";
import { MenuParentType } from "./Menu";

type KeyboardNavigationProps = {
  setCurrentSubmenu: (value: MenuParentType) => void;
  subMenu: MenuParentType;
} & (
  | {
      menu: MenuType;
      type: "menu";
      controlParentMenu?: undefined;
    }
  | {
      menu: MenuItemType;
      type: "submenu";
      controlParentMenu: (action: "close" | "prev" | "next") => void;
    }
);

export const useKeyboardNavigation = ({
  menu,
  type,
  setCurrentSubmenu,
  controlParentMenu,
  subMenu,
}: KeyboardNavigationProps) => {
  const menuRef = useRef<HTMLUListElement>(null);

  const items = useRef<Array<React.RefObject<HTMLLIElement>>>([]);
  const focusedItem = useRef<number>(0);

  const currentMenuItem = () =>
    type === "submenu"
      ? menu.child_items?.[focusedItem.current]
      : type === "menu"
        ? menu.items?.[focusedItem.current]
        : undefined;

  const focusNext = () => {
    if (focusedItem.current === items.current.length - 1) {
      focusedItem.current = 0;
    } else {
      focusedItem.current++;
    }
    focusCurrentItem();
  };
  const focusPrevious = () => {
    if (focusedItem.current === 0) {
      focusedItem.current = items.current.length - 1;
    } else {
      focusedItem.current--;
    }
    focusCurrentItem();
  };
  const focusCurrentItem = () => {
    if (currentMenuItem()?.child_items) {
      items.current[focusedItem.current].current
        ?.querySelector("button")
        ?.focus();
    } else {
      items.current[focusedItem.current].current?.querySelector("a")?.focus();
    }
    setCurrentSubmenu(currentMenuItem());
  };

  const keyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (
      event.key === "ArrowRight" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Escape"
    ) {
      event.preventDefault();
      event.stopPropagation();

      //do not include "Enter" to keep links working
    }

    switch (event.key) {
      case "ArrowRight":
        if (type === "menu") focusNext();

        if (type === "submenu") {
          if (currentMenuItem()?.child_items) {
            setCurrentSubmenu(currentMenuItem());

            setTimeout(() => {
              (
                items.current[focusedItem.current].current?.querySelector(
                  "ul > li > a",
                ) as HTMLElement
              )?.focus();
            }, 50);
          } else {
            controlParentMenu("next");
          }
        }
        break;

      case "ArrowLeft":
        if (type === "menu") focusPrevious();
        if (type === "submenu") controlParentMenu("prev");
        break;

      case "ArrowDown":
        if (type === "submenu") focusNext();
        break;

      case "Enter":
        if (type === "menu") {
          setCurrentSubmenu(menu.items?.[focusedItem.current]);

          setTimeout(() => {
            (
              items.current[focusedItem.current].current?.querySelector(
                ":scope ul > li > a, :scope ul > li > button",
              ) as HTMLElement
            )?.focus();
          }, 50);
        }
        break;

      case "Escape":
        if (type === "submenu") controlParentMenu("close");
        break;

      case "ArrowUp":
        if (type === "submenu") focusPrevious();
        break;
    }
  };

  const controlMenu = (action: "close" | "prev" | "next") => {
    if (action === "prev") focusPrevious();
    if (action === "next") focusNext();

    if (action === "close") {
      setCurrentSubmenu(subMenu);
      items.current[focusedItem.current].current
        ?.querySelector("button")
        ?.focus();
    }
  };

  const firstItemFocused = () => {
    focusedItem.current = 0;
  };

  return {
    menuRef,
    items,
    focusedItem,
    focusNext,
    focusPrevious,
    focusCurrentItem,
    keyDown,
    controlMenu,
    firstItemFocused,
  };
};
